import { useMemo } from "react";
import useSWR from "swr";

import { usePaginationControls } from "@ds-cps/ui";

import {
  ApplicationJsonApiResponse,
  ApplicationsEndpointResponseWithErrors
} from "@application/model/ApplicationJsonModel";

import { organizationApplicationEndpoints } from "@api/Endpoints";
import { fetcher } from "@utils/swr-utils";

import { CreateParamsForGetApplications } from "./CreateParamsForGetApplications";

export function useGetOrganizationApplications(organizationId: string) {
  const { pageIndex, pageSize } = usePaginationControls();

  // Tech Note. In main component, sorting of table entries done via JavaScript aka Frontend Sorting
  const params = new CreateParamsForGetApplications();
  params.withPagination(pageSize, pageIndex);

  const url =
    organizationApplicationEndpoints.get(organizationId) +
    `?${params.toString()}`;

  const { data, error, isLoading, mutate } =
    useSWR<ApplicationsEndpointResponseWithErrors>(url, fetcher, {
      revalidateOnFocus: true
    });

  return useMemo(
    () => ({
      associatedApplications: data?.applications,
      isLoading,
      error,
      mutate
    }),
    [data, isLoading, error, mutate]
  );
}

export function filter(
  applications: ApplicationJsonApiResponse[] | undefined,
  searchValue: string | undefined
): ApplicationJsonApiResponse[] {
  if (!applications) return [];

  if (!searchValue) return applications;

  const value = searchValue.toLocaleLowerCase();

  return applications.filter(item =>
    item.data.name.toLocaleLowerCase().includes(value)
  );
}

export function sort(
  applications: ApplicationJsonApiResponse[] | null,
  activeSort: string | undefined,
  sortDirection: "ascending" | "descending" | undefined
): ApplicationJsonApiResponse[] {
  if (!applications) return [];

  if (!activeSort) return applications;

  const result = [...applications].sort(
    (a: ApplicationJsonApiResponse, b: ApplicationJsonApiResponse): number => {
      let nameA: string | number | undefined;
      let nameB: string | number | undefined;
      if (activeSort) {
        nameA = a.data.name;
        nameB = b.data.name;
      }

      if (typeof nameA === "string" && typeof nameB === "string") {
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      }
      return 0;
    }
  );

  if (sortDirection === "descending") return result.reverse();

  return result;
}
